/**
 * Storefront Content Section
 *
 * iter-3c — Collects vendor-level storefront-v3 content fields:
 *   - company short/long descriptions
 *   - comparison statement (above the comparison block on the homepage)
 *   - pricing mode toggle (live vs illustrative)
 *   - hero block (headline, subhead, CTA, up to 3 image URLs)
 *   - FAQs (up to 20 entries, feeds FAQPage JSON-LD)
 *
 * All fields are Free-tier. Anything left blank renders nothing on the
 * storefront. No invented data, no placeholder copy.
 */

import { useEffect, useState } from 'react'
import api from '@/services/api'
import BokunPrefillBanner from './BokunPrefillBanner'
import { useIsSuperAdmin } from '@/hooks/usePermission'

interface FaqEntry {
  question: string
  answer: string
  isVisible: boolean
  // iter-9f: source tracking. 'auto' = generated by the Bokun-derived
  // deriveVendorContent path. 'vendor' = added or edited by hand.
  source: 'auto' | 'vendor'
  // Pristine snapshot of the auto-generated text. Used to detect when an
  // 'auto' entry has been edited so we can flip the badge to
  // 'Edited (was auto-generated)' before save.
  originalQuestion?: string
  originalAnswer?: string
}

interface ContentForm {
  companyShortDescription: string
  companyLongDescription: string
  comparisonStatement: string
  pricingMode: 'live' | 'illustrative'
  heroHeadline: string
  heroSubhead: string
  heroCtaLabel: string
  heroCtaUrl: string
  heroImagesRaw: string
  // iter-13f: hero video fields.
  heroVideoUrl: string
  heroVideoProvider: '' | 'file' | 'youtube' | 'vimeo'
  heroVideoPoster: string
  faqs: FaqEntry[]
  // iter-9f: server-locked floor on visible auto-FAQ entries.
  faqsAutoMinimum: number
  // True when FAQs are platform defaults shown before first save.
  faqsAreSuggestedDefaults: boolean
}

const EMPTY_FORM: ContentForm = {
  companyShortDescription: '',
  companyLongDescription: '',
  comparisonStatement: '',
  pricingMode: 'live',
  heroHeadline: '',
  heroSubhead: '',
  heroCtaLabel: '',
  heroCtaUrl: '',
  heroImagesRaw: '',
  heroVideoUrl: '',
  heroVideoProvider: '',
  heroVideoPoster: '',
  faqs: [],
  faqsAutoMinimum: 0,
  faqsAreSuggestedDefaults: false,
}

interface Props {
  vendorId: string
}

export default function StorefrontContentSection({ vendorId }: Props) {
  const [form, setForm] = useState<ContentForm>(EMPTY_FORM)
  const [isBokunVendor, setIsBokunVendor] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  // iter-9f: dedicated saving/regenerating flags so the FAQ section's
  // buttons can show their own loading state without locking the rest of
  // the content form.
  const [savingFaqs, setSavingFaqs] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [faqError, setFaqError] = useState<string | null>(null)
  const [faqSuccess, setFaqSuccess] = useState<string | null>(null)
  const isSuperAdmin = useIsSuperAdmin()

  useEffect(() => {
    if (!vendorId) return

    api.vendors.getCheckoutConfig(vendorId)
      .then((res) => {
        const payload = res.data?.data || res.data || {}
        const content = payload.content
        setIsBokunVendor(!!payload.integrations?.bokun?.connected)
        if (!content) return
        setForm({
          companyShortDescription: content.company_short_description || '',
          companyLongDescription: content.company_long_description || '',
          comparisonStatement: content.comparison_statement || '',
          pricingMode: content.pricing_mode === 'illustrative' ? 'illustrative' : 'live',
          heroHeadline: content.hero?.headline || '',
          heroSubhead: content.hero?.subhead || '',
          heroCtaLabel: content.hero?.cta_label || '',
          heroCtaUrl: content.hero?.cta_url || '',
          heroImagesRaw: (content.hero?.images || []).join('\n'),
          heroVideoUrl: content.hero?.video_url || '',
          heroVideoProvider:
            (content.hero?.video_provider as any) || '',
          heroVideoPoster: content.hero?.video_poster || '',
          faqs: (content.faqs || []).map((f: any) => {
            const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
            return {
              question: f.question || '',
              answer: f.answer || '',
              isVisible: f.is_visible !== false,
              source: src,
              // Pristine snapshot only for 'auto' entries so we can detect
              // edits before save. 'vendor' entries have no pristine
              // baseline to compare against.
              originalQuestion: src === 'auto' ? f.question || '' : undefined,
              originalAnswer: src === 'auto' ? f.answer || '' : undefined,
            }
          }),
          faqsAutoMinimum:
            typeof content.faqs_auto_minimum === 'number'
              ? content.faqs_auto_minimum
              : 0,
          faqsAreSuggestedDefaults: !!content.faqs_are_suggested_defaults,
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [vendorId])

  const update = <K extends keyof ContentForm>(field: K, value: ContentForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setError(null)
    setSuccess(null)
  }

  const updateFaq = (index: number, patch: Partial<FaqEntry>) => {
    setForm((prev) => {
      const next = [...prev.faqs]
      next[index] = { ...next[index], ...patch }
      return { ...prev, faqs: next }
    })
    setError(null)
    setSuccess(null)
  }

  const addFaq = () => {
    if (form.faqs.length >= 20) return
    setForm((prev) => ({
      ...prev,
      faqs: [
        ...prev.faqs,
        // Newly added entries are vendor-source by definition.
        { question: '', answer: '', isVisible: true, source: 'vendor' },
      ],
    }))
    setFaqError(null)
    setFaqSuccess(null)
  }

  const isPristineAutoFaq = (faq: FaqEntry) => {
    if (faq.source !== 'auto') return false
    const qEdited = (faq.originalQuestion ?? '').trim() !== faq.question.trim()
    const aEdited = (faq.originalAnswer ?? '').trim() !== faq.answer.trim()
    return !qEdited && !aEdited
  }

  const countAutoEntries = (faqs: FaqEntry[]) =>
    faqs.filter((f) => isPristineAutoFaq(f)).length

  const removeFaq = (index: number) => {
    setForm((prev) => {
      const faq = prev.faqs[index]
      const autoEntries = countAutoEntries(prev.faqs)
      // Auto rows at the floor cannot be deleted; hide them from the storefront
      // instead so Save still succeeds and the public page updates.
      if (
        isPristineAutoFaq(faq) &&
        prev.faqsAutoMinimum > 0 &&
        autoEntries <= prev.faqsAutoMinimum
      ) {
        const next = [...prev.faqs]
        next[index] = { ...next[index], isVisible: false }
        return { ...prev, faqs: next }
      }
      return { ...prev, faqs: prev.faqs.filter((_, i) => i !== index) }
    })
    setFaqError(null)
    setFaqSuccess(null)
  }

  const moveFaq = (index: number, direction: -1 | 1) => {
    setForm((prev) => {
      const target = index + direction
      if (target < 0 || target >= prev.faqs.length) return prev
      const next = [...prev.faqs]
      ;[next[index], next[target]] = [next[target], next[index]]
      return { ...prev, faqs: next }
    })
  }

  const handleSave = async () => {
    if (!vendorId) return

    setError(null)
    setSuccess(null)
    setSaving(true)

    try {
      // Hero images: one URL per line, blanks dropped.
      const heroImages = form.heroImagesRaw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean)

      if (heroImages.length > 3) {
        setError('Hero supports up to 3 images.')
        setSaving(false)
        return
      }

      // iter-9f: FAQs are saved through their dedicated endpoint with
      // floor enforcement. The bulk save no longer round-trips the FAQ
      // array. Anything the user has typed in the FAQ section stays in
      // local form state until they click Save FAQs.

      const payload: Record<string, unknown> = {
        companyShortDescription: form.companyShortDescription.trim(),
        companyLongDescription: form.companyLongDescription.trim(),
        comparisonStatement: form.comparisonStatement.trim(),
        pricingMode: form.pricingMode,
        hero: {
          headline: form.heroHeadline.trim(),
          subhead: form.heroSubhead.trim(),
          ctaLabel: form.heroCtaLabel.trim(),
          ctaUrl: form.heroCtaUrl.trim(),
          images: heroImages,
          // iter-13f: hero video. Empty string means "clear the field".
          videoUrl: form.heroVideoUrl.trim(),
          videoProvider: form.heroVideoProvider || undefined,
          videoPoster: form.heroVideoPoster.trim(),
        },
      }

      await api.vendors.updateContent(vendorId, payload)
      setSuccess('Storefront content saved successfully')
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Failed to save storefront content'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  // iter-9f: dedicated FAQ save. Hits PUT /vendors/{id}/faqs which
  // enforces the auto-FAQ floor server-side and promotes source = 'auto'
  // -> 'vendor' on any text change.
  const handleSaveFaqs = async () => {
    if (!vendorId) return

    setFaqError(null)
    setFaqSuccess(null)
    setSavingFaqs(true)

    try {
      const cleaned = form.faqs
        .filter((f) => f.question.trim() && f.answer.trim())
        .map((f, i) => ({
          question: f.question.trim(),
          answer: f.answer.trim(),
          displayOrder: i,
          isVisible: f.isVisible !== false,
        }))

      const visibleCount = cleaned.filter((f) => f.isVisible).length
      if (visibleCount > 12) {
        setFaqError('Up to 12 FAQs may be visible. Hide some to continue.')
        setSavingFaqs(false)
        return
      }

      const res = await api.vendors.updateFaqs(vendorId, { faqs: cleaned })
      const payload = res.data?.data || res.data || {}
      const saved = (payload.faqs || []) as Array<{
        question: string
        answer: string
        is_visible: boolean
        source?: 'auto' | 'vendor'
      }>

      // Rebuild local state from the server response so badges reflect
      // the freshly-resolved source tags (auto entries with edited text
      // come back as 'vendor').
      setForm((prev) => ({
        ...prev,
        faqsAreSuggestedDefaults: false,
        faqs: saved.map((f) => {
          const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
          return {
            question: f.question || '',
            answer: f.answer || '',
            isVisible: f.is_visible !== false,
            source: src,
            originalQuestion: src === 'auto' ? f.question || '' : undefined,
            originalAnswer: src === 'auto' ? f.answer || '' : undefined,
          }
        }),
      }))
      setFaqSuccess('FAQs saved successfully')
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Failed to save FAQs'
      setFaqError(message)
    } finally {
      setSavingFaqs(false)
    }
  }

  // iter-9f: super-admin only. Re-derives FAQs from Bokun source_data
  // and resets the auto-floor to the regenerated count.
  const handleRegenerateFaqs = async () => {
    if (!vendorId) return
    if (
      !window.confirm(
        'Regenerate FAQs from the Bokun catalogue? This overwrites every existing entry, including vendor edits.',
      )
    ) {
      return
    }

    setFaqError(null)
    setFaqSuccess(null)
    setRegenerating(true)

    try {
      const res = await api.vendors.regenerateFaqs(vendorId)
      const payload = res.data?.data || res.data || {}
      const fresh = (payload.faqs || []) as Array<{
        question: string
        answer: string
        is_visible: boolean
        source?: 'auto' | 'vendor'
      }>
      const newFloor =
        typeof payload.faqs_auto_minimum === 'number'
          ? payload.faqs_auto_minimum
          : 0

      setForm((prev) => ({
        ...prev,
        faqsAutoMinimum: newFloor,
        faqs: fresh.map((f) => {
          const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
          return {
            question: f.question || '',
            answer: f.answer || '',
            isVisible: f.is_visible !== false,
            source: src,
            originalQuestion: src === 'auto' ? f.question || '' : undefined,
            originalAnswer: src === 'auto' ? f.answer || '' : undefined,
          }
        }),
      }))
      setFaqSuccess('FAQs regenerated from Bokun source data')
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Failed to regenerate FAQs'
      setFaqError(message)
    } finally {
      setRegenerating(false)
    }
  }

  if (loading) {
    return (
      <div className="card">
        <h3 style={{ marginBottom: '0.5rem' }}>Storefront Content</h3>
        <p>Loading...</p>
      </div>
    )
  }

  const visibleFaqCount = form.faqs.filter((f) => f.isVisible).length
  // iter-9f/10b: floor applies to auto rows kept in the list, not visibility.
  const autoEntryCount = countAutoEntries(form.faqs)
  const visibleAutoCount = form.faqs.filter(
    (f) => f.isVisible && isPristineAutoFaq(f),
  ).length
  const belowFloor = autoEntryCount < form.faqsAutoMinimum

  return (
    <div className="card">
      <h3 style={{ marginBottom: '0.5rem' }}>Storefront Content</h3>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Long-form copy, hero block, pricing mode, and FAQs shown on the public
        storefront. Anything left blank is omitted rather than guessed.
      </p>

      <BokunPrefillBanner
        show={isBokunVendor}
        message="We have populated your hero, short description, pricing mode, and FAQs from your Bokun catalogue. Long-form description and comparison statement remain blank for you to write or use the AI Draft feature."
      />

      <div style={{ display: 'grid', gap: '1.25rem' }}>
        {/* Descriptions */}
        <div>
          <label style={labelStyle}>Short Description</label>
          <textarea
            value={form.companyShortDescription}
            onChange={(e) => update('companyShortDescription', e.target.value)}
            maxLength={2000}
            rows={3}
            style={textareaStyle}
            placeholder="One paragraph summary of your business."
          />
          <p style={hintStyle}>
            Up to 2000 characters. Used in About Supplier and meta descriptions.
          </p>
        </div>

        <div>
          <label style={labelStyle}>Long Description</label>
          <textarea
            value={form.companyLongDescription}
            onChange={(e) => update('companyLongDescription', e.target.value)}
            maxLength={2000}
            rows={6}
            style={textareaStyle}
            placeholder="Full story, what makes you different, history, mission."
          />
          <p style={hintStyle}>Up to 2000 characters. Renders on the homepage About section.</p>
        </div>

        <div>
          <label style={labelStyle}>Comparison Statement</label>
          <textarea
            value={form.comparisonStatement}
            onChange={(e) => update('comparisonStatement', e.target.value)}
            maxLength={500}
            rows={2}
            style={textareaStyle}
            placeholder='e.g. "We are the only operator running daily small-group tours under €60."'
          />
          <p style={hintStyle}>Up to 500 characters. Shown above the comparison block.</p>
        </div>

        {/* Pricing mode */}
        <div>
          <label style={labelStyle}>Pricing Mode</label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="pricingMode"
                checked={form.pricingMode === 'live'}
                onChange={() => update('pricingMode', 'live')}
              />
              <span>Live — real prices, bookable now</span>
            </label>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="pricingMode"
                checked={form.pricingMode === 'illustrative'}
                onChange={() => update('pricingMode', 'illustrative')}
              />
              <span>Illustrative — sample prices, banner on PDP</span>
            </label>
          </div>
        </div>

        {/* Hero */}
        <div style={sectionStyle}>
          <h4 style={subheadStyle}>Hero Block</h4>
          <p style={hintStyle}>Renders above-the-fold on the storefront homepage.</p>

          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Headline</label>
              <input
                type="text"
                value={form.heroHeadline}
                onChange={(e) => update('heroHeadline', e.target.value)}
                maxLength={120}
                style={inputStyle}
                placeholder="Up to 120 characters"
              />
            </div>

            <div>
              <label style={labelStyle}>Subhead</label>
              <input
                type="text"
                value={form.heroSubhead}
                onChange={(e) => update('heroSubhead', e.target.value)}
                maxLength={240}
                style={inputStyle}
                placeholder="Up to 240 characters"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>CTA Label</label>
                <input
                  type="text"
                  value={form.heroCtaLabel}
                  onChange={(e) => update('heroCtaLabel', e.target.value)}
                  maxLength={40}
                  style={inputStyle}
                  placeholder='e.g. "Browse tours"'
                />
              </div>
              <div>
                <label style={labelStyle}>CTA URL</label>
                <input
                  type="text"
                  value={form.heroCtaUrl}
                  onChange={(e) => update('heroCtaUrl', e.target.value)}
                  maxLength={512}
                  style={inputStyle}
                  placeholder="/tours or https://example.com/page"
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Hero Images (up to 3)</label>
              <textarea
                value={form.heroImagesRaw}
                onChange={(e) => update('heroImagesRaw', e.target.value)}
                rows={3}
                style={textareaStyle}
                placeholder="One URL per line"
              />
              <p style={hintStyle}>
                https URLs or s3:// URIs. Leave blank for no hero image.
              </p>
            </div>

            {/* iter-13f: hero video */}
            <div>
              <label style={labelStyle}>Hero Video URL (optional)</label>
              <input
                type="text"
                value={form.heroVideoUrl}
                onChange={(e) => update('heroVideoUrl', e.target.value)}
                maxLength={512}
                style={inputStyle}
                placeholder="https://example.com/hero.mp4 or a YouTube / Vimeo link"
              />
              <p style={hintStyle}>
                Direct MP4 or WebM, or a YouTube / Vimeo share link. Always
                click-to-play. When set, the video replaces the first hero
                image slot.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Provider</label>
                <select
                  value={form.heroVideoProvider}
                  onChange={(e) =>
                    update('heroVideoProvider', e.target.value as any)
                  }
                  style={inputStyle}
                >
                  <option value="">Auto-detect</option>
                  <option value="file">Direct file (MP4 / WebM)</option>
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Poster Image (optional)</label>
                <input
                  type="text"
                  value={form.heroVideoPoster}
                  onChange={(e) => update('heroVideoPoster', e.target.value)}
                  maxLength={512}
                  style={inputStyle}
                  placeholder="Falls back to the first hero image"
                />
              </div>
            </div>
          </div>
        </div>

        {/* FAQs — iter-9f. Dedicated save path with source badges, an
            auto-FAQ floor enforced server-side, and a super-admin
            regenerate button. */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h4 style={subheadStyle}>FAQs</h4>
            <span style={hintStyle}>
              {form.faqs.length} / 20 total · {visibleFaqCount} / 12 visible
            </span>
          </div>
          <p style={hintStyle}>
            Feeds FAQPage structured data on the storefront homepage. Up to 20
            entries; 12 may be visible at once.
          </p>

          {form.faqsAreSuggestedDefaults && (
            <p
              style={{
                ...hintStyle,
                marginTop: '0.5rem',
                padding: '0.65rem 0.75rem',
                background: 'var(--color-blue-50, #eff6ff)',
                borderRadius: '6px',
                color: 'var(--color-tixxly-navy)',
              }}
            >
              These FAQs match what visitors see on your storefront today. Edit
              them here, then click Save FAQs to keep your changes.
            </p>
          )}

          {form.faqsAutoMinimum > 0 && (
            <p
              style={{
                ...hintStyle,
                color: belowFloor ? 'var(--color-red-600, #b91c1c)' : 'var(--color-gray-600)',
                marginTop: '0.5rem',
              }}
              title="This is the count produced by the very first auto-generation. Editing an auto-FAQ promotes it to vendor-source, which counts against the floor."
            >
              Auto-generated minimum: {form.faqsAutoMinimum} in list ({visibleAutoCount} visible). Remove hides at the floor.
            </p>
          )}

          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
            {form.faqs.map((faq, idx) => {
              const qEdited =
                faq.source === 'auto' &&
                (faq.originalQuestion ?? '').trim() !== faq.question.trim()
              const aEdited =
                faq.source === 'auto' &&
                (faq.originalAnswer ?? '').trim() !== faq.answer.trim()
              const wasAutoEdited = qEdited || aEdited

              let badgeLabel: string
              let badgeColor: string
              let badgeBg: string
              if (faq.source === 'vendor') {
                badgeLabel = 'Vendor-added'
                badgeColor = 'var(--color-tixxly-navy)'
                badgeBg = 'var(--color-blue-50, #eff6ff)'
              } else if (wasAutoEdited) {
                badgeLabel = 'Edited (was auto-generated)'
                badgeColor = 'var(--color-orange-700, #c2410c)'
                badgeBg = 'var(--color-orange-50, #fff7ed)'
              } else {
                badgeLabel = 'Auto-generated'
                badgeColor = 'var(--color-gray-700)'
                badgeBg = 'var(--color-gray-100)'
              }

              return (
                <div key={idx} style={faqCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <strong style={{ fontSize: '0.85rem' }}>FAQ #{idx + 1}</strong>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.45rem',
                          borderRadius: '999px',
                          background: badgeBg,
                          color: badgeColor,
                          fontWeight: 500,
                        }}
                      >
                        {badgeLabel}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button type="button" onClick={() => moveFaq(idx, -1)} disabled={idx === 0} style={miniButtonStyle}>↑</button>
                      <button type="button" onClick={() => moveFaq(idx, 1)} disabled={idx === form.faqs.length - 1} style={miniButtonStyle}>↓</button>
                      <button type="button" onClick={() => removeFaq(idx)} style={{ ...miniButtonStyle, color: 'var(--color-red-600, #b91c1c)' }}>Remove</button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) => updateFaq(idx, { question: e.target.value })}
                      maxLength={240}
                      style={inputStyle}
                      placeholder="Question"
                    />
                    <textarea
                      value={faq.answer}
                      onChange={(e) => updateFaq(idx, { answer: e.target.value })}
                      maxLength={1500}
                      rows={3}
                      style={textareaStyle}
                      placeholder="Answer"
                    />
                    <label style={{ ...radioLabelStyle, fontSize: '0.8rem' }}>
                      <input
                        type="checkbox"
                        checked={faq.isVisible}
                        onChange={(e) => updateFaq(idx, { isVisible: e.target.checked })}
                      />
                      <span>Visible on storefront</span>
                    </label>
                  </div>
                </div>
              )
            })}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={addFaq}
                disabled={form.faqs.length >= 20}
                style={{
                  ...miniButtonStyle,
                  padding: '0.5rem 1rem',
                  background:
                    form.faqs.length >= 20
                      ? 'var(--color-gray-200)'
                      : 'var(--color-gray-100)',
                }}
              >
                {form.faqs.length >= 20 ? 'Limit reached' : 'Add FAQ'}
              </button>
              <button
                type="button"
                onClick={handleSaveFaqs}
                disabled={savingFaqs || belowFloor}
                title={
                  belowFloor
                    ? `At least ${form.faqsAutoMinimum} auto-generated FAQs must stay in the list. Restore a deleted entry, or have a super-admin Regenerate.`
                    : undefined
                }
                style={{
                  padding: '0.5rem 1.25rem',
                  background:
                    savingFaqs || belowFloor
                      ? 'var(--color-gray-400)'
                      : 'var(--color-tixxly-navy)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor:
                    savingFaqs || belowFloor ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                {savingFaqs ? 'Saving...' : 'Save FAQs'}
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleRegenerateFaqs}
                  disabled={regenerating}
                  title="Re-derive every FAQ from the Bokun catalogue and reset the auto-FAQ floor. Super-admin only."
                  style={{
                    padding: '0.5rem 1.25rem',
                    background: regenerating
                      ? 'var(--color-gray-400)'
                      : 'var(--color-gray-100)',
                    color: 'var(--color-gray-800)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px',
                    cursor: regenerating ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate FAQs'}
                </button>
              )}
            </div>

            {faqError && (
              <p style={{ color: 'var(--color-red-600, #b91c1c)', fontSize: '0.8rem', margin: 0 }}>
                {faqError}
              </p>
            )}
            {faqSuccess && (
              <p style={{ color: 'var(--color-green-600, #15803d)', fontSize: '0.8rem', margin: 0 }}>
                {faqSuccess}
              </p>
            )}
          </div>
        </div>

        {error && (
          <p style={{ color: 'var(--color-red-600, #b91c1c)', fontSize: '0.85rem' }}>{error}</p>
        )}
        {success && (
          <p style={{ color: 'var(--color-green-600, #15803d)', fontSize: '0.85rem' }}>{success}</p>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 1.5rem',
            background: saving ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            width: 'fit-content',
          }}
        >
          {saving ? 'Saving...' : 'Save Content (descriptions, hero, pricing)'}
        </button>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.9rem',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'inherit',
  resize: 'vertical',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-gray-500)',
  marginTop: '0.25rem',
}

const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid var(--color-gray-200)',
  paddingTop: '1rem',
}

const subheadStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  margin: 0,
  color: 'var(--color-gray-800)',
}

const radioLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.85rem',
  color: 'var(--color-gray-700)',
  cursor: 'pointer',
}

const faqCardStyle: React.CSSProperties = {
  padding: '0.75rem',
  background: 'var(--color-gray-50)',
  borderRadius: '6px',
  border: '1px solid var(--color-gray-200)',
}

const miniButtonStyle: React.CSSProperties = {
  background: 'var(--color-gray-100)',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '4px',
  padding: '0.2rem 0.5rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
}
